import { css } from 'aphrodite';
import React from 'react';
import { Button } from '../../../../../web/components/Button';
import { CompleteCheckIcon } from '../../../../../web/components/svgs/icons/CompleteCheckIcon';
import { FormFieldType, ICustomSurvey, ICustomSurveyResponse } from '../../../../models/Api';
import { styleSheet } from './styles';

interface IProps {
	survey: ICustomSurvey;
	surveyResponse: ICustomSurveyResponse;
	onChangeResponseClick?: () => void;
}

export const CustomSurveyConfirmation = ({ survey, surveyResponse, onChangeResponseClick }: IProps) => {
	const confirmationMessage = React.useMemo(() => {
		const text = survey?.options?.confirmationMessage?.document;
		if (!text) {
			return null;
		}
		// remove lev-content tag
		const textWithoutLevContent = text.replace(/<lev-content[^>]*>/g, '').replace(/<\/lev-content>/g, '');
		// remove all p tag styles
		const textWithoutPStyles = textWithoutLevContent.replace(/<p[^>]*>/g, '<p>').replace(/<\/p>/g, '</p>');
		return <div dangerouslySetInnerHTML={{ __html: textWithoutPStyles }} className={css(styleSheet.thankYouContent)} />;
	}, [survey]);
	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.confirmationContent)}>
				<div className={css(styleSheet.icon)}>
					<CompleteCheckIcon />
				</div>
				<h2 className={css(styleSheet.header)}>Your response has been recorded!</h2>

				{confirmationMessage ?? <p className={css(styleSheet.message)}>Your response has been recorded.</p>}

				<div className={css(styleSheet.responseDetails)}>
					<h3 className={css(styleSheet.detailsHeader)}>Your Response</h3>
					{surveyResponse?.contactInfo?.firstName ? (
						<div className={css(styleSheet.detailItem)}>
							<span className={css(styleSheet.detailLabel)}>Name:</span>
							<span>{`${surveyResponse.contactInfo.firstName} ${surveyResponse.contactInfo.lastName}`}</span>
						</div>
					) : null}
					{surveyResponse?.contactInfo?.email ? (
						<div className={css(styleSheet.detailItem)}>
							<span className={css(styleSheet.detailLabel)}>Email:</span>
							<span>{surveyResponse.contactInfo.email}</span>
						</div>
					) : null}
					{surveyResponse?.contactInfo?.phoneNumber && (
						<div className={css(styleSheet.detailItem)}>
							<span className={css(styleSheet.detailLabel)}>Phone:</span>
							<span>{surveyResponse.contactInfo.phoneNumber}</span>
						</div>
					)}

					{surveyResponse.customProperties && Object.entries(surveyResponse.customProperties).length > 0 && (
						<div className={css(styleSheet.customProperties)}>
							<h4 className={css(styleSheet.customPropertiesHeader)}>Additional Information</h4>
							{Object.entries(surveyResponse.customProperties)?.map(([key, value]) => {
								const field = survey.customForm?.fields?.find(f => f.id === key);
								if (field.fieldType === FormFieldType.MultipleOptions || field.fieldType === FormFieldType.Option) {
									const multipleAnswers = (value?.split(',') ?? [])?.filter(Boolean);
									const answersFromOptions = field.options
										?.filter(o => multipleAnswers?.includes(o.id))
										.map(o => o.label);

									const answer = answersFromOptions?.join(', ') || '';
									return (
										<div key={key} className={css(styleSheet.detailItem)}>
											<span className={css(styleSheet.detailLabel)}>{field?.label || key}:</span>
											<p>{answer}</p>
										</div>
									);
								}
								return (
									<div key={key} className={css(styleSheet.detailItem)}>
										<span className={css(styleSheet.detailLabel)}>{field?.label || key}:</span>
										<p>{value}</p>
									</div>
								);
							})}
						</div>
					)}
				</div>

				{onChangeResponseClick && (
					<div className={css(styleSheet.actionContainer)}>
						<Button type='button' kind='secondary' label='Edit Response' onClick={onChangeResponseClick} />
					</div>
				)}
			</div>
		</div>
	);
};
